import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import DayPicker from 'react-day-picker';

import { getMonths, getWeeksShort } from 'utils/date-localisation';
import { DatePicker } from 'components/datepicker/datepicker';

import 'react-day-picker/lib/style.css';
import styles from './date-filter-button.module.scss';
import { Dispatch, SetStateAction } from 'react';

export type DateFilterPickersProps = {
	showDates: boolean;
	showDatePickerStart: boolean;
	showDatePickerEnd: boolean;
	startDate?: Date;
	endDate?: Date;
	dateHasError?: boolean;
	onStartDateClick: (date: Date) => void;
	onEndDateClick: (date: Date) => void;
	setShowDatePickerStart: Dispatch<SetStateAction<boolean>>;
	setShowDatePickerEnd: Dispatch<SetStateAction<boolean>>;
	setStartDate: Dispatch<SetStateAction<Date | undefined>>;
	setEndDate: Dispatch<SetStateAction<Date | undefined>>;
};

const DateFilterPickers = ({
	showDates,
	startDate,
	endDate,
	showDatePickerStart,
	showDatePickerEnd,
	dateHasError,
	onStartDateClick,
	onEndDateClick,
	setShowDatePickerStart,
	setShowDatePickerEnd,
	setStartDate,
	setEndDate
}: DateFilterPickersProps): React.JSX.Element => {
	const intl = useIntl();
	const router = useRouter();

	const { locale } = router;

	const handleChangeStartDatePicker = (id: string, date: Date | null | undefined): void => {
		if (id) {
			setStartDate(date || undefined);
		}
	};

	const handleChangeEndDatePicker = (id: string, date: Date | null | undefined): void => {
		if (id) {
			setEndDate(date || undefined);
		}
	};

	return (
		<>
			{showDates && (
				<div className={styles.datePickersParent}>
					<div className={styles.datePickerLabel}>{intl.formatMessage({ id: 'components.dateFilter.label' })}</div>
					<div className={styles.datePickers}>
						<div className={styles.datePicker}>
							<label>{intl.formatMessage({ id: 'modules.searchOverview.startDate' })}</label>
							<DatePicker
								id="firstDatepicker"
								name={intl.formatMessage({ id: 'modules.searchOverview.dateFilter.startDate' })}
								showDateIcon
								value={startDate}
								onChange={handleChangeStartDatePicker}
								neverOpen
								initDateFormat="dd MMM yyyy"
								onFocus={() => {
									setShowDatePickerEnd(false);
									setShowDatePickerStart(true);
								}}
							/>
						</div>
						<div className={styles.datePicker}>
							<label>{intl.formatMessage({ id: 'modules.searchOverview.endDate' })}</label>
							<DatePicker
								id="secondDatepicker"
								name={intl.formatMessage({ id: 'modules.searchOverview.dateFilter.endDate' })}
								showDateIcon
								value={endDate}
								onChange={handleChangeEndDatePicker}
								neverOpen
								initDateFormat="dd MMM yyyy"
								minDate={startDate}
								onFocus={() => {
									setShowDatePickerStart(false);
									setShowDatePickerEnd(true);
								}}
							/>
						</div>
					</div>
					{dateHasError && (
						<div className={styles.dateError} role="alert">
							{intl.formatMessage({ id: 'modules.searchOverview.startDateError' })}
						</div>
					)}
				</div>
			)}

			<div className={clsx(styles.dayPickers, showDatePickerStart || showDatePickerEnd ? styles.show : '')}>
				<div className={clsx(styles.dayPicker, showDatePickerStart ? styles.show : '')}>
					<DayPicker
						selectedDays={startDate || new Date()}
						onDayClick={onStartDateClick}
						month={startDate || new Date()}
						locale={locale}
						months={getMonths()}
						weekdaysShort={getWeeksShort()}
						labels={{
							nextMonth: intl.formatMessage({ id: 'components.dateFilter.nextMonth' }),
							previousMonth: intl.formatMessage({ id: 'components.dateFilter.previousMonth' })
						}}
					/>
				</div>
				<div className={clsx(styles.dayPicker, showDatePickerEnd ? styles.show : '')}>
					<DayPicker
						selectedDays={endDate || new Date()}
						onDayClick={onEndDateClick}
						month={endDate || new Date()}
						locale={locale}
						months={getMonths()}
						weekdaysShort={getWeeksShort()}
						labels={{
							nextMonth: intl.formatMessage({ id: 'components.dateFilter.nextMonth' }),
							previousMonth: intl.formatMessage({ id: 'components.dateFilter.previousMonth' })
						}}
					/>
				</div>
			</div>
		</>
	);
};

export default DateFilterPickers;
